<template>
  <div
    class="s-replyPreview"
    :class="{ 's-replyPreview--deleted': isMessageDeleted, '-inInputArea': inInputArea }">
    <div class="s-replyPreview__header">
      <span class="s-replyPreview__username t-caption1 u-colorDarkMediumEmphasis">
        {{ username }}
      </span>
      <button
        v-if="showCancelButton"
        type="button"
        class="s-replyPreview__cancelButton"
        @click="$emit('cancel')">
        <SvgIcon type="close" />
      </button>
    </div>
    <button
      class="s-replyPreview__content"
      :class="{ 's-replyPreview__content--expanded': isExpanded }"
      @click="toggleExpand">
      <template v-if="isMessageDeleted">
        <span class="s-replyPreview__deletedText t-caption1 u-colorDarkMediumEmphasis">
          {{ $t('chat.deleted_message') }}
        </span>
      </template>
      <template v-else>
        <template v-if="isAttachment">
          <div class="s-replyPreview__attachment">
            <SvgIcon
              class="s-replyPreview__attachmentIcon"
              :type="'filetype_' + attachmentType" />
            <div class="o-flex -align-center -end -gap1">
              <span class="s-replyPreview__attachmentName t-caption1">{{ message.metadata?.file_name }}</span>
              <span class="s-replyPreview__attachmentSize t-caption2 u-colorDarkMediumEmphasis">
                {{ fileSize | kb2human }}
              </span>
            </div>
          </div>
        </template>
        <template v-else>
          <span class="s-replyPreview__text t-caption1">
            <template v-for="(part, index) in messageParts">
              <span
                v-if="part.type === 'text'"
                :key="'text-' + index"
                v-safe-chat-html="part.content" />
              <img
                v-else-if="part.type === 'emoji'"
                :key="'emoji-' + index"
                :src="part.src"
                :alt="`:${part.id}:`"
                class="c-emoji__image" />
            </template>
          </span>
        </template>
      </template>
    </button>
  </div>
</template>

<script setup>
import Vue, { ref, computed } from 'vue'

import constants from '@/constants'
import { useChatStore } from '@/modules/chat/chatStore'
import { useChatThreadStore } from '@/modules/chat/chatThreadStore'
import { splitTextByCustomEmoji, sanitizeMessageContent } from '@/modules/chat/logic/chatMessageContent'

const props = defineProps({
  message: {
    type: Object,
    required: true,
  },
  parentMessage: {
    type: Object,
    default: null,
  },
  username: {
    type: String,
    required: true,
  },
  showCancelButton: {
    type: Boolean,
    default: false,
  },
  isDeleted: {
    type: Boolean,
    default: false,
  },
  inInputArea: {
    type: Boolean,
    default: false,
  },
})

defineEmits(['cancel'])

// Get store for tracking expanded state (only when not in input area)
const chatStore = !props.inInputArea ? useChatStore() : null
const chatThreadStore = chatStore ? useChatThreadStore(chatStore.chatThread.azure_chat_thread_id) : null

// Use store-based expanded state when in messages, local state when in input area,
// to keep the expanded state in sync when the component is re-rendered, and also for size-dependencies update
const localExpanded = ref(false)
const isExpanded = computed(() => {
  if (props.inInputArea || !chatThreadStore || !props.parentMessage) {
    return localExpanded.value
  }
  return !!chatThreadStore.expandedReplyPreviews[props.parentMessage.id]
})

// Check both the prop and the message object for deleted state
const isMessageDeleted = computed(() => {
  return props.isDeleted || props.message?.deleted === true
})

const toggleExpand = (event) => {
  event.stopPropagation()
  if (!isMessageDeleted.value) {
    if (props.inInputArea || !chatThreadStore || !props.parentMessage) {
      localExpanded.value = !localExpanded.value
    } else {
      Vue.set(chatThreadStore.expandedReplyPreviews, props.parentMessage.id, !chatThreadStore.expandedReplyPreviews[props.parentMessage.id])
    }
  }
}

const isAttachment = computed(() => {
  return !!props.message.metadata?.file_id
})

const attachmentType = computed(() => {
  const icons = {
    [constants.LEARNINGMATERIALS.TYPE_VIDEO]: 'video',
    [constants.LEARNINGMATERIALS.TYPE_AUDIO]: 'audio',
    [constants.LEARNINGMATERIALS.TYPE_DOCUMENT]: 'unknown',
    [constants.LEARNINGMATERIALS.TYPE_DOCUMENT_WORD]: 'text-document',
    [constants.LEARNINGMATERIALS.TYPE_DOCUMENT_IMAGE]: 'png',
    [constants.LEARNINGMATERIALS.TYPE_DOCUMENT_PDF]: 'pdf',
    [constants.LEARNINGMATERIALS.TYPE_DOCUMENT_POWERPOINT]: 'presentation',
    [constants.LEARNINGMATERIALS.TYPE_DOCUMENT_SHEET]: 'sheet',
    [constants.LEARNINGMATERIALS.TYPE_ZIP]: 'zip',
  }
  const fileType = props.message.metadata?.file_type
  return icons[fileType ? +fileType : 0] || 'unknown'
})

const fileSize = computed(() => {
  if (!props.message.metadata?.file_size) {
    return 0
  }
  const fileSizeValue = parseFloat(props.message.metadata?.file_size.replace(',', '.'))
  return (fileSizeValue / 1000).toFixed(2)
})

const messageParts = computed(() => {
  if (!props.message.content || isAttachment.value) {
    return []
  }
  const plainText = sanitizeMessageContent(props.message.content)
  return splitTextByCustomEmoji(plainText)
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/config';

.s-replyPreview {
  display: flex;
  flex-direction: column;
  padding: $spacing-2 $spacing-2 $spacing-1;
  background-color: $color-primary_transparent_10;
  border-radius: $spacing-2;

  &.-inInputArea {
    border-radius: $spacing-3 $spacing-3 0 0;
  }
}

.s-replyPreview--deleted {
  background-color: $color-light-gray2;
}

.s-replyPreview__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.s-replyPreview__username {
  font-weight: 500;
  line-height: 1.25;
}

.s-replyPreview__cancelButton {
  display: flex;
  align-items: center;
  justify-content: center;
  width: $spacing-4;
  height: $spacing-4;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  color: $color-dark_medium_emphasis;

  &:hover {
    color: $color-dark;
  }

  .c-icon {
    width: $spacing-3;
    height: $spacing-3;
  }
}

.s-replyPreview__content {
  cursor: pointer;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  text-align: start;
  width: 100%;

  &:not(.s-replyPreview__content--expanded) {
    .s-replyPreview__text {
      display: -webkit-box;
      -webkit-line-clamp: 1;
      line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.s-replyPreview__text {
  word-break: break-word;
  line-height: 1.25;
}

.s-replyPreview__deletedText {
  color: $color-gray;
  font-style: italic;
}

.s-replyPreview__attachment {
  display: flex;
  align-items: center;
  gap: $spacing-1;
}

.s-replyPreview__attachmentIcon {
  width: $spacing-4;
  height: $spacing-4;
  flex-shrink: 0;
}

.s-replyPreview__attachmentName {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 150px;

  @media #{$mq-tablet-portrait} {
    max-width: fit-content;
  }

  @media #{$mq-desktop-small} {
    max-width: 290px;
  }
}
</style>
